/**
 * The classic page's warning strips and job cards, on the new job page (go
 * core, 11/3). The parity audit found these "silently gone": staff who relied
 * on them had to remember to look somewhere else. Same components, same
 * reads, same rules as classic; each one shows nothing when it has nothing
 * to say.
 */
import { useAuth } from "@/lib/auth";
import { isWorkOrderLooking } from "@/lib/signed-work-order";
import { BookingScheduleBanner } from "@/components/booking-schedule-banner";
import { JobTaskFlagStrip } from "@/components/job-task-flag";
import { JobCheckupStrip } from "@/components/job-checkup-strip";
import { JobCustomerCheck } from "@/components/move-job-customer";
import { JobHandoffStrip } from "@/components/closeout/handoff";
import { JobCardHoldPanel } from "@/components/card-hold/card-hold";
import { JobMarksStrip } from "../detail/job-marks-strip";
import { SiteCheckPick } from "../detail/site-check-pick";
import { NoWorkOrderBanner } from "../detail/no-work-order-banner";
import { QuoteScopeOffer } from "../detail/summary-card";
import { JobPoCard } from "../detail/po-card";
import { JobPartsCard } from "../detail/parts-card";
import { PermitCard } from "../detail/permit-card";
import { AfterHoursCard } from "../detail/after-hours-card";
import { RegistrationWarningsCard } from "../detail/registration-warnings";
import type { JobDetail } from "../detail/types";
import { usePaperworkLists } from "./fresh-paperwork";

/** Booking lock, wrong customer, hand-off, tech marks, card hold, site check, no WO, task flags, check-up. */
export function FreshStrips({ job }: { job: JobDetail }) {
    const { isOffice, can } = useAuth();
    const canManageJobs = can("jobs.manage");
    const { lists } = usePaperworkLists(job.id);
    const jobLabel = `Job #${job.display_number || job.id.slice(0, 8)}`;
    return (
        <div className="grid gap-2 empty:hidden" data-testid="fresh-strips">
            {isOffice && <BookingScheduleBanner jobId={job.id} status={job.status} />}
            <JobCustomerCheck jobId={job.id} />
            {isOffice && <JobHandoffStrip jobId={job.id} jobName={jobLabel} />}
            <JobMarksStrip job={job} canHandleFlag={canManageJobs} />
            {isOffice && <JobCardHoldPanel jobId={job.id} />}
            <SiteCheckPick jobId={job.id} needed={!!job.site_check_needed} canConfirm={canManageJobs} />
            {job.wo_missing && (
                <NoWorkOrderBanner
                    jobId={job.id}
                    isOffice={isOffice}
                    candidate={
                        lists.documents
                            .filter((a) => /\.pdf$/i.test(a.file_name) && isWorkOrderLooking(a))
                            .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null
                    }
                />
            )}
            <JobTaskFlagStrip jobId={job.id} />
            <JobCheckupStrip jobId={job.id} />
            {isOffice && job.source_estimate_id && <QuoteScopeOffer jobId={job.id} />}
        </div>
    );
}

/** Purchase orders, parts, after hours, permits, tester registrations. */
export function FreshJobCards({ job }: { job: JobDetail }) {
    const { isOffice, can } = useAuth();
    const canManageJobs = can("jobs.manage");
    return (
        <div className="grid gap-3 empty:hidden" data-testid="fresh-job-cards">
            {isOffice && (
                <JobPoCard jobId={job.id} sourceEstimateId={job.source_estimate_id ?? null} jobStatus={job.status} />
            )}
            <JobPartsCard jobId={job.id} canManage={isOffice} />
            {isOffice && <AfterHoursCard jobId={job.id} canManage={canManageJobs} />}
            <PermitCard jobId={job.id} canManage={canManageJobs} />
            {isOffice && <RegistrationWarningsCard jobId={job.id} />}
        </div>
    );
}
